import { ref } from "vue"
import useAsclepius from "@common/composables/api/asclepius"
import {
  AppNameEnum,
  BaseUser,
  LoginResponse,
  PasswordLoginRequest,
  UserTypeEnum
} from "@vitable/asclepius-api-client-ts"
import { version } from "../../package.json"
import { useUserStore } from "@/store/user-store"

const VITABLE_EMAIL_DOMAIN = "@vitablehealth.com"

function getCompanyDashboardUserType(emailOrPhone: string): UserTypeEnum {
  return emailOrPhone.trim().toLowerCase().endsWith(VITABLE_EMAIL_DOMAIN)
    ? UserTypeEnum.VitableAdmin
    : UserTypeEnum.CompanyAdmin
}

// The forgot-password flow names the account it entered; signed-in screens
// name the current user, since the backend never resets by session alone
function getPasswordResetIdentity(emailOrPhone?: string): { email_or_phone: string; user_type: UserTypeEnum } {
  const identifier = emailOrPhone?.trim()
  if (identifier) {
    return { email_or_phone: identifier, user_type: getCompanyDashboardUserType(identifier) }
  }
  const user = useUserStore().user
  if (!user) throw new Error("Please sign in again to change your password.")
  return { email_or_phone: user.email, user_type: user.user_type }
}

export const useGetMe = () => {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  const me = ref<BaseUser | null>(null)

  async function getMe() {
    await callWithResponse(() => getAuthenticationApi().getMe({}))
    me.value = callResult.value.getData().me
  }

  return {
    getMe,
    getMeResult: callResult,
    me
  }
}

export type PasswordLoginOutcome =
  | { kind: "session"; user: BaseUser; accessToken: string; refreshToken: string }
  | { kind: "twoFactorRequired" }

/**
 * Performs authentication of the user with a password (V2 plane).
 *
 * 2FA completion resubmits the OTP code WITHOUT the password (the V2
 * contract is strict: password or otp_code, never both). Any challenge
 * other than otp is treated as an invalid login attempt.
 */
export function useLogin() {
  const { getAuthenticationApi, callWithResponse } = useAsclepius()

  type LoginParams = {
    emailOrPhone: string
    password: string
    twoFactorToken?: string
  }

  const isLoginPending = ref(false)

  async function login(params: LoginParams): Promise<PasswordLoginOutcome> {
    isLoginPending.value = true
    const identifier = params.emailOrPhone.trim()
    const request: PasswordLoginRequest = {
      email_or_phone: identifier,
      user_type: getCompanyDashboardUserType(identifier),
      password: params.twoFactorToken ? undefined : params.password,
      otp_code: params.twoFactorToken,
      app_name: AppNameEnum.CompanyDashboard,
      app_version: version
    }

    try {
      const response = await callWithResponse<LoginResponse>(
        async () => await getAuthenticationApi().passwordLogin({ passwordLoginRequest: request })
      )

      const session = response.login_session
      if (session) {
        return {
          kind: "session",
          user: session.user,
          accessToken: session.access_token,
          refreshToken: session.refresh_token
        }
      }
      if (response.challenge?.challenge_type === "otp") {
        return { kind: "twoFactorRequired" }
      }
      // email_required / unknown challenges have no meaning on this dashboard
      throw new Error("Unable to complete login. Please try again.")
    } finally {
      isLoginPending.value = false
    }
  }

  return {
    isLoginPending,
    login
  }
}

export const useLoginPasswordlessToEmployerDash = () => {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function loginPasswordlessToEmployerDash(emailOrPhone: string, otp: Nullable<string>) {
    // A null otp requests a code (the otp challenge in the response is the
    // expected outcome); a present otp verifies it and mints the session
    await callWithResponse(async () => {
      const response = await getAuthenticationApi().passwordlessLogin({
        passwordlessLoginRequest: {
          email_or_phone: emailOrPhone,
          user_type: UserTypeEnum.CompanyAdmin,
          otp_code: otp,
          app_name: AppNameEnum.CompanyDashboard,
          app_version: version
        }
      })

      // Any challenge besides otp (e.g. a shared-phone email_required) can't
      // be completed here — failing beats a code screen where nothing arrives
      const { login_session, challenge } = response.data
      if (!login_session && challenge?.challenge_type !== "otp") {
        throw new Error("Invalid login attempt, please try again.")
      }
      return response
    })
  }

  return {
    loginPasswordlessToEmployerDash,
    loginPasswordlessResult: callResult
  }
}

export const useRequestPasswordResetCode = () => {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function requestPasswordResetCode(emailOrPhone?: string) {
    await callWithResponse(async () => {
      const response = await getAuthenticationApi().requestPasswordChange({
        requestPasswordChangeRequest: getPasswordResetIdentity(emailOrPhone)
      })
      // A phone shared by several accounts cannot be resolved; the user must retry with their email
      if (response.data.challenge?.challenge_type !== "otp") {
        throw new Error("Please use the email address on your account to continue.")
      }
      return response
    })
  }

  return {
    requestPasswordResetCode,
    requestPasswordResetCodeResult: callResult
  }
}

export const useUpdatePassword = () => {
  const { getAuthenticationApi, callWithResponse, callResult } = useAsclepius()

  async function updatePassword(newPassword: string, code: string, emailOrPhone?: string) {
    await callWithResponse(() =>
      getAuthenticationApi().resetPassword({
        resetPasswordRequest: { new_password: newPassword, otp_code: code, ...getPasswordResetIdentity(emailOrPhone) }
      })
    )
  }

  return {
    updatePassword,
    updatePasswordResult: callResult
  }
}
